import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../hooks/useCart';
import { useCurrency } from '../../hooks/useCurrency';
import { useLanguage } from '../../hooks/useLanguage';
import { useState, useEffect } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useNavigate } from 'react-router-dom';
import TrustBadges from '../home/components/TrustBadges';
import { formatCOP, getDisplayPrice, getSecondaryPrice } from '../../lib/currency';
import OfficeSeoJsonLd from './components/SeoJsonLd';

export default function OfficePage() {
  const { addItem } = useCart();
  const { currency } = useCurrency();
  const { t } = useLanguage();
  const [addedId, setAddedId] = useState<string | null>(null);
  const navigate = useNavigate();

  const {
    containerRef: appsRef,
    getAnimationClass: getAppsAnimClass,
    getAnimationStyle: getAppsAnimStyle,
  } = useScrollAnimation();

  const {
    containerRef: productsRef,
    getAnimationClass: getProductsAnimClass,
    getAnimationStyle: getProductsAnimStyle,
  } = useScrollAnimation();

  /* --------------------------- Data definitions --------------------------- */
  const officeApps = [
    {
      name: 'Word',
      icon: 'ri-file-word-line',
      color: 'from-[#2B579A] to-[#1a3d6e]',
      description: t('office.word_desc'),
    },
    {
      name: 'Excel',
      icon: 'ri-file-excel-line',
      color: 'from-[#217346] to-[#165232]',
      description: t('office.excel_desc'),
    },
    {
      name: 'PowerPoint',
      icon: 'ri-file-ppt-line',
      color: 'from-[#D24726] to-[#a8361c]',
      description: t('office.powerpoint_desc'),
    },
    {
      name: 'Outlook',
      icon: 'ri-mail-line',
      color: 'from-[#0078D4] to-[#005a9e]',
      description: t('office.outlook_desc'),
    },
    {
      name: 'OneDrive',
      icon: 'ri-cloud-line',
      color: 'from-[#0078D4] to-[#004e8c]',
      description: t('office.onedrive_desc'),
    },
    {
      name: 'Teams',
      icon: 'ri-team-line',
      color: 'from-[#6264A7] to-[#464775]',
      description: t('office.teams_desc'),
    },
  ];

  const officeLicenses = [
    {
      id: 'office2026ltsc1v2',
      name: 'Office 365 última versión - 1 cuenta',
      description: 'Licencia de por vida - 1 usuario',
      price: 149990,
      oldPrice: 374975,
      discount: '-60%',
      image:
        'https://public.readdy.ai/ai/img_res/0c37555de554fb2159ddcb0247f6317f.jpg',
      badge: 'NUEVO',
      type: 'Suscripción anual - 1 usuario',
      apps: ['Word', 'Excel', 'PowerPoint', 'Outlook', 'Teams', 'OneDrive 1TB'],
      salesCount: 72,
      isNew2026: true,
    },
    {
      id: 'office2021ltsc5v2',
      name: 'Office 365 última versión - 5 cuentas',
      description: 'Licencia de por vida con un descuento del 10%',
      price: 149990,
      oldPrice: 374975,
      discount: '-60%',
      image:
        'https://readdy.ai/api/search-image?query=spectacular%20eye%20catching%20Microsoft%20Office%202026%20Professional%20Plus%20premium%20product%20display%20with%20vibrant%20electric%20blue%20orange%20gradient%20holographic%20glow%20effects%20floating%203D%20digital%20license%20card%20pack%20of%20five%20featuring%20large%20prominent%20colorful%20Microsoft%20Office%20logo%20with%20Word%20Excel%20PowerPoint%20Outlook%20icons%20in%20bright%20blue%20green%20orange%20colors%20modern%20futuristic%20design%20dramatic%20studio%20lighting%20with%20neon%20accents%20dark%20elegant%20background%20with%20radiant%20light%20beams%20luxury%20software%20packaging%20high%20impact%20commercial%20photography%20attention%20grabbing%20visual&width=400&height=400&seq=off2026ltsc5specialv3&orientation=squarish',
      badge: 'NUEVO 5 PCs',
      type: 'Suscripción anual - 5 usuarios',
      apps: ['Word', 'Excel', 'PowerPoint', 'Outlook', 'Teams', 'OneDrive 1TB'],
      multiplyBy5: true,
      salesCount: 124,
      isNew2026: true,
    },
    {
      id: 'office2024pro',
      name: 'Licencia Original Office 2024 Professional Plus',
      description: 'Licencia de por vida',
      price: 69900,
      oldPrice: 229900,
      discount: '-70%',
      image:
        'https://readdy.ai/api/search-image?query=vibrant%20modern%20Microsoft%20Office%202024%20Professional%20Plus%20product%20showcase%20with%20glowing%20orange%20red%20gradient%20holographic%20effects%20floating%20digital%20license%20key%20card%20sleek%20futuristic%20design%20dramatic%20studio%20lighting%20dark%20background%20with%20light%20rays%20premium%20software%20packaging%203D%20render%20high%20quality%20commercial%20photography&width=400&height=400&seq=offpro2024new1&orientation=squarish',
      badge: 'Más Vendido',
      type: 'Licencia de por vida - 1 PC',
      apps: ['Word', 'Excel', 'PowerPoint', 'Outlook', 'Access', 'Publisher'],
      salesCount: 59,
    },
    {
      id: 'office2024hb',
      name: 'Licencia Office 2024 Home and Business',
      description: 'Licencia de por vida',
      price: 710300,
      oldPrice: 1250000,
      discount: '-43%',
      image:
        'https://readdy.ai/api/search-image?query=stunning%20Microsoft%20Office%202024%20Home%20Business%20premium%20product%20display%20with%20vibrant%20teal%20cyan%20gradient%20holographic%20glow%20floating%20digital%20license%20card%20modern%20minimalist%20design%20dramatic%20lighting%20dark%20elegant%20background%20with%20light%20beams%20luxury%20software%20packaging%203D%20visualization%20high%20end%20commercial%20photography&width=400&height=400&seq=offhb2024new1&orientation=squarish',
      badge: 'Premium',
      type: 'Licencia de por vida - 5 PC',
      apps: ['Word', 'Excel', 'PowerPoint', 'Outlook'],
      salesCount: 45,
    },
    {
      id: 'office2024ltsc1',
      name: 'Licencia Office 2024 Pro Plus LTSC 1 cuenta',
      description: 'Licencia de por vida - 1 PC',
      price: 89900,
      oldPrice: 229900,
      discount: '-61%',
      image:
        'https://readdy.ai/api/search-image?query=striking%20Microsoft%20Office%202024%20LTSC%20Professional%20Plus%20single%20user%20product%20showcase%20with%20bold%20dark%20orange%20amber%20gradient%20holographic%20effects%20floating%20digital%20license%20key%20card%20sleek%20modern%20design%20dramatic%20studio%20lighting%20dark%20background%20with%20warm%20glowing%20accents%20premium%20software%20display%203D%20render%20professional%20commercial%20photography&width=400&height=400&seq=offltsc2024single1&orientation=squarish',
      badge: 'LTSC',
      type: 'Licencia de por vida - 1 PC',
      apps: ['Word', 'Excel', 'PowerPoint', 'Outlook', 'Access', 'Publisher'],
      salesCount: 58,
    },
    {
      id: 'office2024ltsc5',
      name: 'Licencia Office 2024 Pro Plus LTSC 5 cuentas',
      description: 'Con 5 cuentas, licencia de por vida con un descuento del 10%',
      price: 89900,
      oldPrice: 229900,
      discount: '-61%',
      image:
        'https://readdy.ai/api/search-image?query=spectacular%20Microsoft%20Office%202024%20LTSC%20Professional%20Plus%20five%20users%20bundle%20product%20display%20with%20fiery%20red%20orange%20gradient%20holographic%20glow%20floating%20digital%20license%20cards%20pack%20modern%20dynamic%20design%20dramatic%20lighting%20dark%20background%20with%20flame%20like%20light%20effects%20premium%20multi%20user%20software%20packaging%203D%20visualization%20high%20quality%20commercial%20photography&width=400&height=400&seq=offltsc2024new1&orientation=squarish',
      badge: '5 PCs',
      type: 'Licencia de por vida - 5 PCs',
      apps: ['Word', 'Excel', 'PowerPoint', 'Outlook', 'Access', 'Publisher'],
      multiplyBy5: true,
      salesCount: 93,
    },
    {
      id: 'office2021pro',
      name: 'Licencia Original Office 2021 Professional Plus',
      description: 'Licencia de por vida',
      price: 49950,
      oldPrice: 199900,
      discount: '-75%',
      image:
        'https://readdy.ai/api/search-image?query=impressive%20Microsoft%20Office%202021%20Professional%20Plus%20product%20display%20with%20deep%20blue%20navy%20gradient%20holographic%20glow%20floating%20digital%20license%20card%20sophisticated%20modern%20design%20dramatic%20lighting%20dark%20elegant%20background%20with%20light%20effects%20premium%20software%20packaging%203D%20visualization%20high%20quality%20commercial%20photography&width=400&height=400&seq=offpro2021new1&orientation=squarish',
      badge: 'Mejor Precio',
      type: 'Licencia de por vida - 1 PC',
      apps: ['Word', 'Excel', 'PowerPoint', 'Outlook', 'Access', 'Publisher'],
      salesCount: 32,
    },
    {
      id: 'office2021hb',
      name: 'Licencia Office 2021 Home and Business',
      description: 'Licencia de por vida',
      price: 370953,
      oldPrice: 706563,
      discount: '-47%',
      image:
        'https://readdy.ai/api/search-image?query=eye%20catching%20Microsoft%20Office%202021%20Home%20Business%20product%20showcase%20with%20bright%20emerald%20green%20gradient%20holographic%20effects%20floating%20digital%20license%20key%20modern%20sleek%20design%20dramatic%20studio%20lighting%20dark%20background%20with%20glowing%20accents%20premium%20software%20display%203D%20render%20professional%20commercial%20photography&width=400&height=400&seq=offhb2021new1&orientation=squarish',
      badge: 'Oferta',
      type: 'Licencia de por vida - 5 PC',
      apps: ['Word', 'Excel', 'PowerPoint', 'Outlook'],
      salesCount: 34,
    },
    {
      id: 'office2021ltsc1',
      name: 'Licencia Office 2021 Pro Plus LTSC 1 cuenta',
      description: 'Licencia de por vida - 1 PC',
      price: 59950,
      oldPrice: 219900,
      discount: '-73%',
      image:
        'https://readdy.ai/api/search-image?query=elegant%20Microsoft%20Office%202021%20LTSC%20Professional%20Plus%20single%20user%20enterprise%20edition%20product%20display%20with%20sophisticated%20silver%20charcoal%20gradient%20holographic%20glow%20floating%20digital%20license%20card%20modern%20corporate%20design%20dramatic%20lighting%20dark%20background%20with%20metallic%20shine%20effects%20premium%20business%20software%20packaging%203D%20visualization%20high%20end%20commercial%20photography&width=400&height=400&seq=offltsc2021single1&orientation=squarish',
      badge: 'LTSC',
      type: 'Licencia de por vida - 1 PC',
      apps: ['Word', 'Excel', 'PowerPoint', 'Outlook', 'Access', 'Publisher'],
      salesCount: 45,
    },
    {
      id: 'office2021ltsc',
      name: 'Licencia Office 2021 Pro Plus LTSC 5 cuentas',
      description: 'Con 5 cuentas, licencia de por vida con un descuento del 10%',
      price: 59950,
      oldPrice: 219900,
      discount: '-73%',
      image:
        'https://readdy.ai/api/search-image?query=professional%20Microsoft%20Office%202021%20LTSC%20Professional%20Plus%20enterprise%20edition%20product%20showcase%20with%20metallic%20silver%20gray%20gradient%20holographic%20effects%20floating%20digital%20license%20card%20pack%20of%20five%20modern%20corporate%20design%20dramatic%20studio%20lighting%20dark%20background%20with%20chrome%20accents%20premium%20business%20software%20display%203D%20render%20high%20end%20commercial%20photography&width=400&height=400&seq=offltsc2021new1&orientation=squarish',
      badge: 'LTSC 5 PCs',
      type: 'Licencia de por vida - 5 PCs',
      apps: ['Word', 'Excel', 'PowerPoint', 'Outlook'],
      multiplyBy5: true,
      salesCount: 87,
    },
  ];

  /* -------------------------- Helper functions -------------------------- */
  const handleAddToCart = (product: typeof officeLicenses[0]) => {
    if (!product?.id) return;
    try {
      if (product.multiplyBy5) {
        const totalPrice = product.price * 5;
        const totalOldPrice = product.oldPrice ? product.oldPrice * 5 : 0;
        const savings = totalOldPrice - totalPrice;

        addItem({
          id: product.id,
          name: product.name,
          type: 'Licencia Digital - Pack 5 cuentas',
          price: totalPrice,
          oldPrice: totalOldPrice > 0 ? totalOldPrice : undefined,
          image: product.image,
          savingsDescription:
            totalOldPrice > 0
              ? `Precio original 5 cuentas: $${totalOldPrice.toLocaleString(
                  'es-CO'
                )} — Ahorras: $${savings.toLocaleString('es-CO')}`
              : undefined,
        });
        setAddedId(product.id);
        setTimeout(() => {
          setAddedId(null);
          navigate('/carrito');
        }, 800);
        return;
      }

      addItem({
        id: product.id,
        name: product.name,
        type: product.type,
        price: product.price,
        oldPrice: product.oldPrice,
        image: product.image,
      });
      setAddedId(product.id);
      setTimeout(() => setAddedId(null), 1500);
    } catch (err) {
      console.error('Error adding item to cart:', err);
    }
  };

  const badgeColors: Record<string, string> = {
    [t('product.best_seller')]:
      'bg-gradient-to-r from-[#D24726] to-[#e85d3a]',
    [t('product.premium')]:
      'bg-gradient-to-r from-[#7B61FF] to-[#9b85ff]',
    [t('product.offer')]:
      'bg-gradient-to-r from-[#D24726] to-[#e85d3a]',
    [t('product.best_price')]:
      'bg-gradient-to-r from-[#217346] to-[#2d9a5e]',
    LTSC: 'bg-gradient-to-r from-[#555] to-[#777]',
    '5 PCs': 'bg-gradient-to-r from-[#0078D4] to-[#2196F3]',
    '365': 'bg-gradient-to-r from-[#D24726] to-[#ff6f47]',
  };

  const quickFeatures = [
    {
      icon: 'ri-mail-send-line',
      text: t('office.email_instant'),
      color: 'text-[#D24726]',
    },
    {
      icon: 'ri-key-2-line',
      text: t('office.original_key'),
      color: 'text-[#217346]',
    },
    {
      icon: 'ri-refresh-line',
      text: t('office.replacement_warranty'),
      color: 'text-[#0078D4]',
    },
    {
      icon: 'ri-guide-line',
      text: t('office.install_guide'),
      color: 'text-[#7B61FF]',
    },
  ];

  // -------------------------------------------------------
  // Responsive handling for the apps list inside a product card
  // -------------------------------------------------------
  const [isMobile, setIsMobile] = useState<boolean>(false);
  useEffect(() => {
    const checkSize = () => setIsMobile(window.innerWidth < 640);
    checkSize();
    window.addEventListener('resize', checkSize);
    return () => window.removeEventListener('resize', checkSize);
  }, []);

  /* ------------------------------- Render ------------------------------- */
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <OfficeSeoJsonLd />
      <Header />

      {/* Hero */}
      <section className="relative h-[240px] sm:h-[360px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=sleek%20modern%20workspace%20desk%20with%20large%20ultrawide%20monitor%20displaying%20colorful%20productivity%20dashboard%20charts%20documents%20warm%20ambient%20lighting%20minimalist%20scandinavian%20interior%20design%20wooden%20desk%20plants%20coffee%20cup%20soft%20natural%20light%20from%20window%20high%20end%20photography%20cinematic%20composition%20warm%20tones%20orange%20and%20teal%20accents&width=1920&height=360&seq=officehero2025v2&orientation=landscape"
          alt="Microsoft Office - Licencias originales"
          title="Microsoft Office licencias originales Colombia"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        <div className="relative container mx-auto px-4 h-full flex items-center">
          <div className="text-white w-full max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1 sm:px-4 sm:py-1.5 mb-3 sm:mb-5">
              <i className="ri-verified-badge-fill text-[#D24726]"></i>
              <span className="text-xs sm:text-sm font-medium">
                {t('office.original_licenses')}
              </span>
            </div>
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-2 sm:mb-4 leading-tight">
              {t('office.title')
                .split('Microsoft Office')[0]
                .trim()}
              <span className="text-[#D24726]"> Microsoft Office</span>
            </h1>
            <p className="text-sm sm:text-lg text-white/90 max-w-xl leading-relaxed">
              {t('office.subtitle')}
            </p>
            <div className="flex flex-wrap items-center gap-3 sm:gap-6 mt-4 sm:mt-6">
              <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-white/80">
                <i className="ri-shield-check-line text-[#D24726]"></i>
                <span>{t('office.lifetime_warranty')}</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-white/80">
                <i className="ri-flashlight-line text-[#D24726]"></i>
                <span>{t('office.instant_delivery')}</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-white/80">
                <i className="ri-customer-service-2-line text-[#D24726]"></i>
                <span>{t('office.support_247')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Características de Office */}
      <section className="py-10 sm:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
              {t('office.apps_title')}
            </h2>
            <p className="text-gray-600 text-xs sm:text-sm max-w-2xl mx-auto">
              {t('office.apps_subtitle')}
            </p>
          </div>
          <div
            ref={appsRef}
            className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-5"
          >
            {officeApps.map((app, index) => (
              <div
                key={app.name}
                data-anim-index={index}
                style={getAppsAnimStyle(index)}
                className={`group bg-white border border-gray-100 rounded-xl p-3 sm:p-5 text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-500 ease-out cursor-default ${getAppsAnimClass(
                  index
                )}`}
              >
                <div
                  className={`w-10 h-10 sm:w-14 sm:h-14 mx-auto mb-2 sm:mb-4 rounded-xl bg-gradient-to-br ${app.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}
                >
                  <i className={`${app.icon} text-white text-lg sm:text-2xl`}></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-xs sm:text-sm mb-1 sm:mb-2">
                  {app.name}
                </h3>
                <p className="text-[10px] sm:text-xs text-gray-500 leading-relaxed hidden sm:block">
                  {app.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ventajas rápidas */}
      <section className="py-6 sm:py-8 bg-[#FAFAFA] border-y border-gray-100">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
            {quickFeatures.map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-2 sm:gap-3 bg-white rounded-lg px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-100"
              >
                <div
                  className={`w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center flex-shrink-0 ${item.color}`}
                >
                  <i className={`${item.icon} text-base sm:text-xl`}></i>
                </div>
                <span className="text-[10px] sm:text-sm font-medium text-gray-700 leading-tight">
                  {item.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEW 2026 Featured Section */}
      <section className="py-6 sm:py-10 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-2 mb-4 sm:mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D24726] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#D24726]"></span>
            </span>
            <h2 className="text-base sm:text-xl font-bold text-gray-900">
              <i className="ri-fire-fill text-[#D24726] mr-1 text-sm"></i>
              Office 365 última versión — Edición Especial
            </h2>
            <span className="text-[9px] sm:text-[10px] font-bold text-[#D24726] bg-[#D24726]/10 px-2 py-0.5 rounded-full uppercase tracking-wide whitespace-nowrap">Nuevo</span>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-5 max-w-3xl mx-auto" data-product-shop>
            {officeLicenses.filter(p => p.isNew2026).map((product) => {
              const display = getDisplayPrice(product.price, product.oldPrice, currency);
              const secondary = getSecondaryPrice(product.price, product.oldPrice, currency);
              return (
                <div
                  key={product.id}
                  className="relative bg-white border-2 border-[#D24726]/30 rounded-xl overflow-hidden hover:shadow-lg hover:border-[#D24726]/60 transition-all duration-300 group cursor-pointer flex flex-col"
                >
                  {/* Compact ribbon */}
                  <div className="bg-gradient-to-r from-[#D24726] to-orange-500 text-white text-center py-1 text-[8px] sm:text-[10px] font-bold tracking-wider uppercase">
                    OFFICE 365
                  </div>

                  <div className="relative">
                    {product.discount && (
                      <span className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 bg-gray-900 text-white text-[9px] sm:text-xs font-bold px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded-full whitespace-nowrap z-10">
                        {product.discount}
                      </span>
                    )}
                    <div className="w-full h-28 sm:h-40 bg-gradient-to-b from-orange-50/30 to-white flex items-center justify-center p-3 sm:p-5">
                      <img
                        src={product.image}
                        alt={product.name}
                        title={`${product.name} licencia original`}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  </div>

                  <div className="p-3 sm:p-4 flex flex-col flex-1">
                    <h3 className="text-[11px] sm:text-sm font-semibold text-gray-900 mb-1 line-clamp-2 min-h-[28px] sm:min-h-[36px]">
                      {product.name}
                    </h3>
                    <p className="text-[10px] sm:text-xs text-gray-500 mb-1.5 flex items-center gap-1">
                      <i className="ri-information-line"></i>
                      {product.type}
                    </p>
                    {product.description && (
                      <p className="text-[10px] sm:text-xs text-[#D24726] font-medium mb-1.5">
                        {product.description}
                      </p>
                    )}
                    <div className="flex items-center gap-1 mb-1.5">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-yellow-400 text-[10px] sm:text-xs"></i>
                      ))}
                      <span className="text-[9px] sm:text-xs text-gray-500 ml-0.5">(5.0)</span>
                    </div>
                    <div className="flex items-center gap-1 mb-2">
                      <i className="ri-shopping-bag-line text-[10px] sm:text-xs text-orange-500"></i>
                      <span className="text-[10px] sm:text-xs text-gray-500">{product.salesCount.toLocaleString('es-CO')} vendidos</span>
                    </div>

                    <div className="mt-auto">
                      <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2 mb-0.5">
                        <span className="text-base sm:text-lg font-bold text-gray-900">
                          {display.main}
                        </span>
                        {display.oldMain && (
                          <span className="text-[10px] sm:text-xs text-gray-400 line-through">
                            {display.oldMain}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 mb-2 sm:mb-3">
                        <p className="text-[10px] sm:text-xs text-emerald-600 font-medium">
                          ≈ {secondary.current}
                        </p>
                        {secondary.old && (
                          <p className="text-[10px] sm:text-xs text-gray-400 line-through hidden sm:block">
                            {secondary.old}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleAddToCart(product)}
                        className={`w-full py-2 sm:py-2.5 rounded-lg font-medium text-[11px] sm:text-sm transition-all duration-200 whitespace-nowrap flex items-center justify-center gap-1 sm:gap-2 ${
                          addedId === product.id
                            ? 'bg-[#217346] text-white shadow-lg shadow-green-200'
                            : 'bg-gradient-to-r from-[#D24726] to-orange-500 text-white hover:from-[#b83d20] hover:to-[#D24726] shadow-sm hover:shadow-lg hover:shadow-red-100'
                        }`}
                      >
                        {addedId === product.id ? (
                          <>
                            <i className="ri-check-line"></i>
                            {t('product.added')}
                          </>
                        ) : (
                          <>
                            <i className="ri-shopping-cart-line"></i>
                            <span className="hidden sm:inline">
                              {t('product.add_to_cart')}
                            </span>
                            <span className="sm:hidden">{t('product.add')}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Diferencia Office LTSC vs Normal */}
      <section className="py-6 sm:py-10 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-xl p-4 sm:p-6">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gray-600 rounded-lg flex items-center justify-center">
                <i className="ri-information-line text-white text-lg sm:text-xl"></i>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">¿Qué diferencia hay entre Office LTSC y Normal?</h3>
                <div className="space-y-3 sm:space-y-4 text-xs sm:text-sm text-gray-600">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <i className="ri-checkbox-circle-fill text-gray-600 flex-shrink-0 mt-0.5 text-sm sm:text-base"></i>
                    <p><strong className="text-gray-800">Office LTSC:</strong> Versión completa PREMIUM con soporte extendido (Long-Term Servicing Channel). Actualizaciones de seguridad por 5 años, sin cambios de funciones. Ideal para estabilidad.</p>
                  </div>
                  <div className="flex items-start gap-2 sm:gap-3">
                    <i className="ri-checkbox-circle-fill text-gray-600 flex-shrink-0 mt-0.5 text-sm sm:text-base"></i>
                    <p><strong className="text-gray-800">Office Normal:</strong> Versión estándar con todas las funciones. Actualizaciones de seguridad regulares. Perfecto para uso diario.</p>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-gray-700 font-medium"><strong>Ambas versiones incluyen:</strong> Word, Excel, PowerPoint, Outlook, Access y Publisher — licencia de por vida.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Products */}
      <main className="flex-1 py-8 sm:py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-6 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
              {t('office.our_licenses')}
            </h2>
            <p className="text-gray-600 text-xs sm:text-sm">
              {t('office.choose_license')}
            </p>
          </div>
          <div
            ref={productsRef}
            className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6"
            data-product-shop
          >
            {officeLicenses.filter(p => !p.isNew2026).map((product, index) => {
              const display = getDisplayPrice(product.price, product.oldPrice, currency);
              const secondary = getSecondaryPrice(product.price, product.oldPrice, currency);
              return (
                <div
                  key={product.id}
                  data-anim-index={index}
                  style={getProductsAnimStyle(index)}
                  className={`bg-white border border-gray-100 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-500 ease-out group cursor-pointer flex flex-col ${getProductsAnimClass(
                    index
                  )}`}
                >
                  <div className="relative">
                    {product.badge && (
                      <span
                        className={`absolute top-2 left-2 sm:top-3 sm:left-3 ${badgeColors[product.badge] ||
                          'bg-[#D24726]'} text-white text-[9px] sm:text-xs font-semibold px-1.5 py-0.5 sm:px-3 sm:py-1 rounded-full whitespace-nowrap z-10 shadow-sm`}
                      >
                        {product.badge}
                      </span>
                    )}
                    {product.discount && (
                      <span className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-gray-900 text-white text-[9px] sm:text-xs font-bold px-1.5 py-0.5 sm:px-2.5 sm:py-1 rounded-full whitespace-nowrap z-10">
                        {product.discount}
                      </span>
                    )}
                    <div className="w-full h-32 sm:h-52 bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-3 sm:p-6">
                      <img
                        src={product.image}
                        alt={product.name}
                        title={`${product.name} licencia original`}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  </div>
                  <div className="p-3 sm:p-5 flex flex-col flex-1">
                    <h3 className="text-[11px] sm:text-sm font-semibold text-gray-900 mb-1 sm:mb-1.5 line-clamp-2 min-h-[28px] sm:min-h-[40px]">
                      {product.name}
                    </h3>
                    <p className="text-[10px] sm:text-xs text-gray-500 mb-2 sm:mb-3 flex items-center gap-1 sm:gap-1.5">
                      <i className="ri-information-line"></i>
                      {product.type}
                    </p>
                    {product.description && (
                      <p className="text-[10px] sm:text-xs text-[#D24726] font-medium mb-2 sm:mb-3">
                        {product.description}
                      </p>
                    )}
                    <div className="flex items-center gap-1 mb-2 sm:mb-3">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-yellow-400 text-[10px] sm:text-sm"></i>
                      ))}
                      <span className="text-[9px] sm:text-xs text-gray-500 ml-0.5">(5.0)</span>
                    </div>
                    <div className="flex items-center gap-1 mb-1.5 sm:mb-2">
                      <i className="ri-shopping-bag-line text-[10px] sm:text-xs text-orange-500"></i>
                      <span className="text-[10px] sm:text-xs text-gray-500">{product.salesCount.toLocaleString('es-CO')} vendidos</span>
                    </div>
                    <div className="flex flex-wrap gap-0.5 sm:gap-1 mb-2 sm:mb-4">
                      {product.apps
                        .slice(0, isMobile ? 3 : product.apps.length)
                        .map((app) => (
                          <span
                            key={app}
                            className="text-[8px] sm:text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 sm:px-2 rounded-full font-medium"
                          >
                            {app}
                          </span>
                        ))}
                      {isMobile && product.apps.length > 3 && (
                        <span className="text-[8px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full font-medium">
                          +{product.apps.length - 3}
                        </span>
                      )}
                    </div>
                    <div className="mt-auto">
                      <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2 mb-0.5">
                        <span className="text-base sm:text-xl font-bold text-gray-900">
                          {display.main}
                        </span>
                        {display.oldMain && (
                          <span className="text-[10px] sm:text-xs text-gray-400 line-through">
                            {display.oldMain}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2 mb-2 sm:mb-4">
                        <p className="text-[10px] sm:text-xs text-emerald-600 font-medium">
                          ≈ {secondary.current}
                        </p>
                        {secondary.old && (
                          <p className="text-[10px] sm:text-xs text-gray-400 line-through hidden sm:block">
                            {secondary.old}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleAddToCart(product)}
                        className={`w-full py-2 sm:py-2.5 rounded-lg font-medium text-[11px] sm:text-sm transition-all duration-200 whitespace-nowrap flex items-center justify-center gap-1 sm:gap-2 ${
                          addedId === product.id
                            ? 'bg-[#217346] text-white shadow-lg shadow-green-200'
                            : 'bg-[#D24726] text-white hover:bg-[#b83d20] shadow-sm hover:shadow-lg hover:shadow-red-100'
                        }`}
                      >
                        {addedId === product.id ? (
                          <>
                            <i className="ri-check-line"></i>
                            {t('product.added')}
                          </>
                        ) : (
                          <>
                            <i className="ri-shopping-cart-line"></i>
                            <span className="hidden sm:inline">
                              {t('product.add_to_cart')}
                            </span>
                            <span className="sm:hidden">{t('product.add')}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      <TrustBadges />

      {/* Contacto */}
      <section className="py-10 sm:py-16 bg-gradient-to-br from-[#0078D4] to-[#00a2ed]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3">
            {t('office.need_help')}
          </h2>
          <p className="text-white/90 text-xs sm:text-sm mb-5 sm:mb-6 max-w-xl mx-auto">
            {t('office.help_desc')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <a
              href="https://wa.me/79534940353"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-[#25D366] font-semibold px-5 py-2.5 sm:px-6 sm:py-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-sm shadow-lg w-full sm:w-auto justify-center"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              {t('office.contact_whatsapp')}
            </a>
            <a
              href="https://t.me/Sup0rteWin"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white/15 text-white font-semibold px-5 py-2.5 sm:px-6 sm:py-3 rounded-lg hover:bg-white/25 transition-colors cursor-pointer whitespace-nowrap text-sm border border-white/30 w-full sm:w-auto justify-center"
            >
              <i className="ri-telegram-line text-lg"></i>
              Telegram
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
